import { css } from '@emotion/react';
import { darken, mix, rgba } from 'polished';
import { colorYiq } from '../_functions';
import { GradientBg } from './_gradients.mixin';
import Mixin from '../../../util/mixin';
import { BoxShadow } from './_box-shadow.mixin';
import { Hover } from './_hover.mixin';
import { FontSize } from '../vendor/_rfs.mixin';
import { BorderRadius } from './_border-radius.mixin';

window.polishedMix = mix;

export class ButtonVariant extends Mixin {
  style({ $ }, [background, border, hoverBackground = undefined, hoverBorder = undefined, activeBackground = undefined, activeBorder = undefined]) {
    hoverBackground = darken(0.075, background);
    hoverBorder = darken(0.1, border);
    activeBackground = darken(0.1, background);
    activeBorder = darken(0.125, border);
    const bgYiq = colorYiq($, background);
    const hoverBgYiq = colorYiq($, hoverBackground);
    const borderBgYiqMix = rgba(mix(0.15, bgYiq, border), 0.5);
    const boxShadow = `0 0 0 ${$.btnFocusWidth} ${borderBgYiqMix}`;

    const rules = [];
    rules.push(css`
      color: ${bgYiq};
      ${new GradientBg(background).include($)};
      border-color: ${border};
      ${new BoxShadow($.btnBoxShadow).include($)};

      ${new Hover().include(
        $,
        css`
          color: ${hoverBgYiq};
          ${new GradientBg(hoverBackground).include($)};
          border-color: ${hoverBorder};
        `
      )}
    `);

    rules.push(css`
      &:focus,
      &.focus {
        color: ${hoverBgYiq};
        ${new GradientBg(hoverBackground).include($)};
        border-color: ${hoverBorder};
        ${$.enableShadows
          ? new BoxShadow($.btnBoxShadow, boxShadow).include($)
          : css`
              // Avoid using mixin so we can pass custom focus shadow properly
              box-shadow: ${boxShadow};
            `}
      }
    `);

    rules.push(css`
      // Disabled comes first so active can properly restyle
      &.disabled,
      &:disabled {
        color: ${bgYiq};
        background-color: ${background};
        border-color: ${border};
        // Remove CSS gradients if they're enabled
        ${$.enableGradients
          ? css`
              background-image: none;
            `
          : css``}
      }
    `);

    rules.push(css`
      &:not(:disabled):not(.disabled):active,
      &:not(:disabled):not(.disabled).active,
      .show > &.dropdown-toggle {
        color: ${colorYiq($, activeBackground)};
        background-color: ${activeBackground};
        ${
          $.enableGradients
            ? css`
                background-image: none; // Remove the gradient for the pressed/active state
              `
            : css``
        }
        border-color: ${activeBorder};

        &:focus {
          ${
            $.enableShadows && $btnActiveBoxShadow !== 'none'
              ? css`
                  ${new BoxShadow($btnActiveBoxShadow, boxShadow).include($)};
                `
              : css`
                  // Avoid using mixin so we can pass custom focus shadow properly
                  box-shadow: ${boxShadow};
                `
          }
        }
      }
    `);

    return css(rules);
  }
}

export class ButtonOutlineVariant extends Mixin {
  style({ $ }, [color, colorHover = undefined, activeBackground = undefined, activeBorder = undefined]) {
    colorHover = colorYiq($, color);
    activeBackground = color;
    activeBorder = color;
    const boxShadow = `0 0 0 ${$.btnFocusWidth} ${rgba(color, 0.5)}`;

    return css`
      color: ${color};
      border-color: ${color};

      ${new Hover().include(
        $,
        css`
          color: ${colorHover};
          background-color: ${activeBackground};
          border-color: ${activeBorder};
        `
      )}

      &:focus,
      &.focus {
        box-shadow: ${boxShadow};
      }

      &.disabled,
      &:disabled {
        color: ${color};
        background-color: transparent;
      }

      &:not(:disabled):not(.disabled):active,
      &:not(:disabled):not(.disabled).active,
      .show > &.dropdown-toggle {
        color: ${colorYiq($, activeBackground)};
        background-color: ${activeBackground};
        border-color: ${activeBorder};

        &:focus {
          ${$.enableShadows && $.btnActiveBoxShadow !== 'none'
            ? css`
                ${new BoxShadow($.btnActiveBoxShadow, `${boxShadow}`).include($)};
              `
            : css`
                // Avoid using mixin so we can pass custom focus shadow properly
                box-shadow: ${boxShadow};
              `}
        }
      }
    `;
  }
}

// Button sizes
export class ButtonSize extends Mixin {
  style({ $ }, [paddingY, paddingX, fontSize, lineHeight, borderRadius]) {
    return css`
      padding: ${paddingY} ${paddingX};
      ${new FontSize(fontSize).include($)};
      line-height: ${lineHeight};
      // Manually declare to provide an override to the browser default
      ${new BorderRadius(borderRadius, 0).include($)};
    `;
  }
}
